import { useState, useEffect, useRef } from 'react';

export const useNotifications = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  // Créer le son de notification (bip sonore)
  useEffect(() => {
    // Créer un contexte audio
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const audioContext = new AudioContext();
    
    const playNotificationSound = () => {
      try {
        // Créer un oscillateur pour le son de cloche
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        // Configuration du son de cloche
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // Fréquence haute
        
        // Envelope pour un son de cloche
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
        
        // Deuxième note pour effet de cloche
        setTimeout(() => {
          const osc2 = audioContext.createOscillator();
          const gain2 = audioContext.createGain();
          
          osc2.connect(gain2);
          gain2.connect(audioContext.destination);
          
          osc2.type = 'sine';
          osc2.frequency.setValueAtTime(600, audioContext.currentTime);
          
          gain2.gain.setValueAtTime(0.2, audioContext.currentTime);
          gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
          
          osc2.start(audioContext.currentTime);
          osc2.stop(audioContext.currentTime + 0.5);
        }, 100);
      } catch (error) {
        console.error('Erreur audio:', error);
      }
    };
    
    audioRef.current = playNotificationSound;
  }, []);

  // Demander la permission pour les notifications du navigateur
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Vérifier les nouvelles demandes toutes les 10 secondes
  useEffect(() => {
    const checkNewQuotes = async () => {
      try {
        const apiUrl = window.location.origin;
        const token = localStorage.getItem('admin_token');
        
        // Ne pas faire de requête si pas de token
        if (!token) {
          return;
        }
        
        const response = await fetch(`${apiUrl}/api/quotes`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        // Si le token est invalide, ne pas traiter
        if (!response.ok) {
          return;
        }
        
        const quotes = await response.json();
        
        // Récupérer les IDs déjà vus depuis localStorage
        const seenIds = JSON.parse(localStorage.getItem('seenQuoteIds') || '[]');
        
        // Compter les nouvelles demandes non vues
        const newQuotes = quotes.filter(q => !seenIds.includes(q.id));
        const newCount = newQuotes.length;
        
        // Si le nombre de nouvelles demandes a augmenté, afficher une notification
        if (newCount > unreadCount && newCount > 0) {
          // Notification du navigateur
          if ('Notification' in window && Notification.permission === 'granted') {
            const latestQuote = newQuotes[0];
            new Notification('🔔 Nouvelle Demande de Devis !', {
              body: `Client: ${latestQuote.name}\nAppareil: ${latestQuote.deviceType} ${latestQuote.brand || ''}\nProblème: ${latestQuote.problem}`,
              icon: '/favicon.ico',
              tag: 'new-quote',
              requireInteraction: true
            });
          }
        }
        
        setUnreadCount(newCount);
        
        // Mettre à jour le titre de la page avec le badge
        if (newCount > 0) {
          document.title = `(${newCount}) Demandes de Devis - Admin`;
        } else {
          document.title = 'Demandes de Devis - Admin';
        }
        
        // Activer ou désactiver le son selon le nombre de demandes non vues
        if (newCount > 0) {
          setIsPlaying(true);
        } else {
          setIsPlaying(false);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification des demandes:', error);
      }
    };
    
    // Vérification immédiate
    checkNewQuotes();
    
    // Puis toutes les 10 secondes
    const interval = setInterval(checkNewQuotes, 10000);
    
    return () => clearInterval(interval);
  }, [unreadCount]);

  // Jouer le son répétitif toutes les 15 secondes tant qu'il y a des demandes non vues
  useEffect(() => {
    // SON DÉSACTIVÉ PAR L'UTILISATEUR
    // Arrêter tout interval existant
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Son désactivé - pas de bip même s'il y a des demandes non vues
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [unreadCount]); // Dépend uniquement de unreadCount

  const markAllAsSeen = async () => {
    try {
      const apiUrl = window.location.origin;
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${apiUrl}/api/quotes`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des devis');
      }
      
      const quotes = await response.json();
      
      // Sauvegarder tous les IDs comme vus
      const allIds = quotes.map(q => q.id);
      localStorage.setItem('seenQuoteIds', JSON.stringify(allIds));
      
      setUnreadCount(0);
      setIsPlaying(false);
      
      // Réinitialiser le titre de la page
      document.title = 'Demandes de Devis - Admin';
    } catch (error) {
      console.error('Erreur lors du marquage comme vu:', error);
    }
  };

  return { unreadCount, isPlaying, markAllAsSeen };
};
